/**
 * ENG-5812 — rewrite the server-rendered `.mcp.json` so the operator's
 * local Claude Code can actually spawn the agent's MCP servers.
 *
 * Why this exists
 * ---------------
 * The redeem endpoint (`POST /impersonate/agent/redeem` in
 * packages/api/src/routes/impersonate.ts) calls `provision()` with
 * `deploymentTarget: 'local_docker'`. The claudecode framework adapter
 * uses `getHomeDir()` (the SERVER's runtime home — `/home/sbx_user1051`
 * on Lambda) and bakes that path into the rendered `.mcp.json`'s
 * `args`. It also leaves `AGT_HOST` / `AGT_API_KEY` / `HOME` empty
 * because those are normally filled by the deployment process, not by
 * provisioning. The artifact is structurally a *Lambda runtime
 * artifact*, not an *operator-side artifact*.
 *
 * When the operator's Claude Code tries to spawn `node
 * /home/sbx_user1051/.augmented/_mcp/index.js`, it ENOENTs — the path
 * doesn't exist on their Mac. Net effect: every MCP server in the
 * rendered persona fails to start, the impersonated agent has zero
 * working tools, and any "list your integrations" answer is the agent
 * narrating from CHARTER.md memory (which is what observably happens
 * — see Sherlock's reproduction in ENG-5812's description).
 *
 * Until the server learns about an "impersonation" deployment target
 * (Scope B), the CLI post-processes the artifact before swapping it
 * into the operator's project. We substitute:
 *
 *   - Any `args` path matching `/home/<x>/.augmented/_mcp/<file>` with
 *     `<cliMcpBundleDir>/<file>`. The CLI ships its own copy of the
 *     channel + gateway MCP bundles via `build:mcp-assets`, so the
 *     binaries are already on disk wherever the operator installed
 *     `@integrity-labs/agt-cli`.
 *   - Empty `env.AGT_HOST` with the API host the redeem was performed
 *     against (so the gateway calls back to the right env).
 *   - Empty `env.AGT_API_KEY` with the impersonation JWT (so the
 *     gateway authenticates as the impersonating principal — the JWT
 *     carries `act_as_agent_id` for attribution).
 *   - Empty `env.HOME` with the operator's `$HOME`.
 *   - Empty `env.AGT_AGENT_ID` / `env.AGT_AGENT_CODE_NAME` with the
 *     redeem-known values — the renderer fills these but they may be
 *     missing on a future server change; defence-in-depth.
 *
 * Pre-populated env values are NOT overwritten — the server's
 * intentional choice wins. Only empty/missing entries get the
 * impersonation-time substitution.
 *
 * Two env keys are the exception and ARE rewritten unconditionally,
 * because the server's value is structurally wrong for the operator
 * (ENG-5818):
 *
 *   - `PATH` is baked at provision time as the SERVER's runtime PATH.
 *     On the redeem Lambda that's an AWS Lambda PATH
 *     (`/var/lang/bin:/usr/local/bin:/usr/bin/:/bin:/opt/bin`) — none of
 *     those dirs hold `node` on a typical macOS nvm/Homebrew host, and
 *     because `env.PATH` overrides the inherited PATH the `command:
 *     "node"` spawn ENOENTs (exit 127) and EVERY server fails to start.
 *     We replace it with an operator-correct PATH (see `operatorPath`).
 *   - `AGT_RUN_ID` is emitted as the literal `${AGT_RUN_ID}` sentinel
 *     for a manager to substitute at spawn time. An impersonation
 *     session has no manager and no `runs` row, so the placeholder is
 *     never expanded. Run-id columns FK `runs(n)`, so the only correct
 *     value is "unset" — we drop the key (the augmented MCP bridge maps
 *     a missing/empty AGT_RUN_ID to a null run id; leaving the literal
 *     would leak a bogus non-empty run id into broker calls).
 *
 * Pure function (no I/O); the caller (`impersonateConnectCommand`) does
 * the file-write. Exported for unit tests.
 */

export interface ImpersonationMcpRewriteContext {
  /** `process.env.HOME` on the operator's machine. */
  operatorHome: string;
  /**
   * ENG-5818: a host-correct `PATH` for the operator's machine, used to
   * overwrite the server-baked (Lambda) PATH so `command: "node"` resolves.
   * The caller builds this from the running node's bin dir prepended to
   * the operator's own `process.env.PATH` (see `resolveOperatorPath` in
   * commands/impersonate.ts).
   */
  operatorPath: string;
  /**
   * Absolute path to the CLI's bundled `dist/mcp/` directory (which
   * holds index.js / slack-channel.js / telegram-channel.js /
   * direct-chat-channel.js via build:mcp-assets). The caller resolves
   * this from `import.meta.url` at runtime.
   */
  cliMcpBundleDir: string;
  /** Impersonation JWT (`bundle.token`). Legacy host-key-slot credential. */
  impersonationToken: string;
  /**
   * ENG-5874: the portable agent-session token (`bundle.agent_session_token`)
   * the augmented MCP server presents directly as Bearer (skipping
   * /host/exchange, which 401s the impersonation JWT). Null for pre-org teams /
   * a non-fatal redeem-side mint failure — the server then falls back to the
   * (broken-for-impersonation) host-key path.
   */
  agentSessionToken: string | null;
  /** API host the redeem was exchanged against (e.g. `https://api.augmented.team`). */
  apiHost: string;
  /** The impersonated agent's UUID. */
  agentId: string;
  /** The impersonated agent's `code_name`. */
  agentCodeName: string;
}

/** Matches `/home/<anything-not-slash>/.augmented/_mcp/<filename>`. */
const SERVER_HOME_MCP_PATH_RE =
  /^\/home\/[^/]+\/\.augmented\/_mcp\/([^/]+)$/;

/**
 * ENG-5953 — server keys whose entries are channel MCP servers, injected by
 * the redeem flow for an agent's configured channels. We stamp
 * `AGT_ACT_AS_AGENT_ID` onto their env so the ENG-5689 egress gate
 * (`isImpersonating()` in packages/mcp/src/impersonation.ts) fails CLOSED —
 * channel sends stay refused-by-default — even if Claude Code ever stops
 * passing the launch env (`buildImpersonateClaudeLaunch` sets the same var)
 * through to MCP children. The operator still opts in to sends with
 * `ENABLE_IMPERSONATION_CHANNELS=true`, which flows in via that same
 * inherited env.
 *
 * ## Stamping a marker is not gating a server (ENG-10753)
 *
 * This set says which entries get the marker. Whether anything READS it is a
 * property of the server on the other end, and for two of these keys the
 * answer was no:
 *
 *   - `msteams` was in this set from the start, and `teams-channel.ts`
 *     contained no occurrence of `isImpersonating()` or
 *     `shouldRefuseImpersonatedEgress`. The fail-closed marker above was
 *     written onto a server that never looked at it, so five egress tools —
 *     reply, react, send_structured, ask_user, upload_file — were reachable
 *     under impersonation while this comment said they were refused by
 *     default. ENG-10753 added the gate; the marker now does what it says.
 *   - `whatsapp` was MISSING from this set entirely, while
 *     `claudecode/index.ts` emits `mcpServers['whatsapp']` for both the kapso
 *     and baileys providers. Added by ENG-10753, alongside that server's gate.
 *
 * `discord` is a third case, and it is the one this file cannot fix. The key
 * is NOT dead — `claudecode/index.ts` does emit a `discord` entry — but what
 * it spawns is `npx -y @anthropic/claude-code-discord`, a third-party server.
 * It will never read `AGT_ACT_AS_AGENT_ID`, so the marker is inert there by
 * construction and no change in this repo can gate its egress. The key is kept
 * rather than removed because removing it would assert the entry is fictional,
 * which is the opposite of true. The only actually fail-closed option for a
 * server we do not own is to omit its entry from the rewritten config under
 * impersonation; that is a product decision, not a cleanup, and is not taken
 * here.
 *
 * Exported so a test can assert this set's contents by equality. Iterating it
 * cannot catch a deleted member — the loop just gets shorter — and a deleted
 * member is exactly the `whatsapp` case above.
 */
export const CHANNEL_SERVER_KEYS = new Set([
  'slack',
  'telegram',
  'msteams',
  'direct-chat',
  'discord',
  'whatsapp',
]);

/**
 * Rewrite the persona `.mcp.json` so the operator's local Claude Code
 * can spawn the MCP servers. Returns a new JSON string; never mutates
 * the input. Throws if the input isn't valid JSON or doesn't have an
 * object-typed `mcpServers` field — the caller already validated the
 * redeem response shape, so a throw here means the server changed its
 * contract.
 */
export function rewriteMcpJsonForImpersonation(
  mcpJson: string,
  ctx: ImpersonationMcpRewriteContext,
): string {
  const parsed = JSON.parse(mcpJson) as unknown;
  if (!isPlainObject(parsed)) {
    throw new Error(
      `rewriteMcpJsonForImpersonation: expected an object at the JSON root, got ${typeof parsed}`,
    );
  }
  const servers = parsed['mcpServers'];
  if (!isPlainObject(servers)) {
    throw new Error(
      'rewriteMcpJsonForImpersonation: expected an object-typed `mcpServers` field',
    );
  }

  const rewrittenServers: Record<string, unknown> = {};
  for (const [serverName, raw] of Object.entries(servers)) {
    rewrittenServers[serverName] = rewriteServerEntry(serverName, raw, ctx);
  }

  const out = { ...parsed, mcpServers: rewrittenServers };
  return JSON.stringify(out, null, 2);
}

function rewriteServerEntry(
  serverName: string,
  raw: unknown,
  ctx: ImpersonationMcpRewriteContext,
): unknown {
  if (!isPlainObject(raw)) return raw;

  // ── ENG-6278: URL-based (http/sse) entries pass through UNCHANGED. ────
  // These are the agent's Composio `composio_*` integration servers (and any
  // other remote-MCP). They carry no `/home/<x>/.augmented/_mcp/` args to
  // path-fix and no `command:"node"` to PATH-fix, and they authenticate via a
  // templated `${COMPOSIO_API_KEY}` header that the launch env resolves
  // (buildImpersonateClaudeLaunch). Crucially we must NOT fold an `env` block
  // onto an http entry — Claude Code's MCP schema rejects unknown fields on
  // http servers and would fail the WHOLE config at startup (the ENG-5074
  // failure mode), killing every server including `augmented`.
  if ('url' in raw) return raw;

  // ── args: replace any /home/<x>/.augmented/_mcp/<file> with the
  //    operator's CLI bundle dir.
  let args = raw['args'];
  if (Array.isArray(args)) {
    args = args.map((arg) => {
      if (typeof arg !== 'string') return arg;
      const m = SERVER_HOME_MCP_PATH_RE.exec(arg);
      if (!m) return arg;
      return `${ctx.cliMcpBundleDir}/${m[1]}`;
    });
  }

  // ── env: fill empty/missing impersonation-time values without
  //    clobbering anything the server explicitly populated.
  const env = isPlainObject(raw['env']) ? { ...raw['env'] } : {};
  fillIfEmpty(env, 'AGT_HOST', ctx.apiHost);
  fillIfEmpty(env, 'AGT_API_KEY', ctx.impersonationToken);
  // ENG-5874: the augmented MCP server prefers this token (used directly as
  // Bearer) over AGT_API_KEY. Only set when redeem returned one.
  if (ctx.agentSessionToken) {
    fillIfEmpty(env, 'AGT_AGENT_SESSION_TOKEN', ctx.agentSessionToken);
  }
  fillIfEmpty(env, 'HOME', ctx.operatorHome);
  fillIfEmpty(env, 'AGT_AGENT_ID', ctx.agentId);
  fillIfEmpty(env, 'AGT_AGENT_CODE_NAME', ctx.agentCodeName);

  // ── ENG-5818 Bug 1: force a host-correct PATH ────────────────────────
  // The server bakes its own runtime PATH (Lambda PATH on the redeem
  // Lambda). It's never right for the operator, so overwrite it
  // unconditionally rather than only-if-empty. Only do so when the
  // server actually emitted a PATH key — a server entry with no PATH
  // inherits the spawn PATH, which is already correct.
  if ('PATH' in env) {
    env['PATH'] = ctx.operatorPath;
  }

  // ── ENG-5818 Bug 3: drop the unsubstituted AGT_RUN_ID placeholder ────
  // No manager + no `runs` row in an impersonation session, so the
  // `${AGT_RUN_ID}` sentinel is never expanded. Run-id columns FK
  // runs(n); the only correct value is "unset". Dropping the key lets
  // consumers fall back to their null-run behaviour instead of treating
  // the literal placeholder as a (truthy, bogus) run id.
  if (env['AGT_RUN_ID'] === '${AGT_RUN_ID}') {
    delete env['AGT_RUN_ID'];
  }

  // ── ENG-5953: fail-closed impersonation marker on channel servers ────
  // Set unconditionally (not fillIfEmpty) so the egress gate engages even
  // if a future redeem build emitted a stale value. Scoped to channel
  // server keys so the `augmented` gateway + native integrations are
  // untouched.
  if (CHANNEL_SERVER_KEYS.has(serverName)) {
    env['AGT_ACT_AS_AGENT_ID'] = ctx.agentId;
  }

  return { ...raw, args, env };
}

/**
 * Set `env[key] = value` when the current value is missing/empty — or
 * when it is the key's own literal `${KEY}` placeholder. ENG-5901
 * Track D: the server now renders secrets (AGT_API_KEY et al.) as
 * `${VAR}` templates for the manager's spawn env to satisfy; an
 * impersonation session has no manager env, and Claude Code hard-fails
 * config parse on an unset defaultless `${VAR}`, so the self-placeholder
 * must be treated as fillable here. Pre-populated concrete values are
 * still never overwritten.
 */
function fillIfEmpty(
  env: Record<string, unknown>,
  key: string,
  value: string,
): void {
  const current = env[key];
  if (
    typeof current !== 'string' ||
    current.length === 0 ||
    current === `\${${key}}`
  ) {
    env[key] = value;
  }
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}
